import jwtDecode from "jwt-decode";
import React, { createContext, useEffect, useMemo, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import authReducer, { authStates } from "../reducers/authReducer";
import { AuthContextType, AuthState } from "../types/authTypes";
import api from "../configs/apiConfig";
import {
    getToken,
    refreshAuthToken,
    setJwtSession,
    setRefreshToken,
    verifyToken,
} from "../utils/jwtFunctions";
import useMessage from "../hooks/useMessage";
import { defaultRoute } from "./CurrentRouteContext";
import { UserRole } from "../enums/Auth";

const initialState: AuthState = {
    user: null,
    isLogged: false,
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const alert = useMessage();
    const [state, dispatch] = useReducer(authReducer, initialState);

    async function loadUserFromMemory() {
        let authToken = getToken();
        if (authToken) {
            const isAuthValid = verifyToken(authToken);
            if (!isAuthValid) {
                authToken = await refreshAuthToken(state.user?.username);
            }
            if (authToken) {
                buildStateFromToken(authToken);
            } else {
                setJwtSession("REM");
            }
        }
        return authToken;
    }

    async function buildStateFromToken(authToken: any) {
        try {
            if (!verifyToken(authToken)) throw new Error("Token Expired");
            const decoded: any = jwtDecode(authToken);
            if (!decoded) throw new Error("Error decoding Auth Token");
            if (decoded.isDisabled) throw new Error("disabled");
            if (decoded.role !== UserRole.ADMIN) throw new Error("notAdmin");

            setJwtSession("SET", authToken);
            dispatch({
                type: authStates.LOGIN,
                payload: {
                    user: {
                        email: decoded.email,
                        username: decoded.username,
                        isDisabled: decoded.isDisabled,
                        role: decoded.role,
                        isTrusted: decoded.isTrusted,
                        isCompleted: decoded.isCompleted,
                        sessionId: decoded.sessionId,
                    },
                    isLogged: true,
                },
            });
            const path = localStorage.getItem("currentRoute") || defaultRoute;
            navigate(path);
        } catch (error: any) {
            await logout();
            switch (error.message) {
                case "disabled":
                    alert.showError(
                        "Your account has been disabled. Please contact " +
                            process.env.REACT_APP_DEFAULT_USER_EMAIL,
                    );
                    break;
                case "notAdmin":
                    alert.showError("You are not authorized");
                    break;
                default:
                    console.log(error.message);
                    break;
            }
        }
    }

    useEffect(() => {
        loadUserFromMemory();
    }, []);

    async function login(uname: string, password: string) {
        const response = await api.post("/login", {
            username: uname,
            password,
        });
        const { username, email, accessToken, refreshToken } = response.data;

        if (!accessToken) {
            throw new Error("Login Error");
        }

        const decoded: any = jwtDecode(accessToken);

        if (!decoded && !decoded?.exp) {
            throw new Error("Error in the Access Token");
        }

        // update the users current data with receiving data
        if (decoded.role !== UserRole.ADMIN) throw new Error("notAdmin");

        setJwtSession("SET", accessToken);
        setRefreshToken("SET", refreshToken);

        dispatch({
            type: authStates.LOGIN,
            payload: {
                user: {
                    username,
                    email,
                    isDisabled: decoded.isDisabled,
                    role: decoded.role,
                    isTrusted: decoded.isTrusted,
                    isCompleted: decoded.isCompleted,
                    sessionId: decoded.sessionId,
                },
                isLogged: true,
            },
        });
    }

    async function clearAuthState() {
        setJwtSession("REM");
        setRefreshToken("REM");
        dispatch({
            type: authStates.LOGOUT,
        });
    }

    async function logout() {
        try {
            await api.post("/logout", {});
        } catch (error) {
            console.log("Error while logging out");
        } finally {
            clearAuthState();
            navigate("/login");
        }
    }

    async function forceLogoutUser() {
        try {
            await new Promise((resolve) => setTimeout(resolve));
        } finally {
            clearAuthState();
            navigate("/login");
        }
    }

    // Auth Token refresh at expiry
    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            const errorMessage = error?.response?.data?.message ?? "";
            if (errorMessage === "Auth Token has expired") {
                try {
                    const newToken = await refreshAuthToken(
                        state.user?.username,
                    );
                    if (newToken) {
                        return api(error.config);
                    } else {
                        throw new Error("Refresh Failed");
                    }
                } catch (refreshError) {
                    throw new Error("Refresh token expired");
                }
            } else {
                return Promise.reject(error);
            }
        },
    );

    // Across Tab Logout watch
    useEffect(() => {
        const handleStorageChange = (event: any) => {
            if (event.key === "login") {
                console.log("User logged in in another tab");
                window.location.reload();
            } else if (event.key === "logout") {
                console.log("User logged out in another tab");
                window.location.reload();
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    const authContextValue = useMemo(
        () => ({
            user: state.user,
            login,
            logout,
            isLogged: state.isLogged,
            forceLogoutUser,
        }),
        [state.user, state.isLogged],
    );

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};
export default AuthContext;
